import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider, Typography } from '@mui/material';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import { logoutAsync } from '../../../store/slices/authSlice';
import LoginForm from './LoginForm';
import SignUpForm from './SignUpForm';
import UserProfile from './UserProfile';
import ResetPasswordModal from './ResetPasswordModal';

/**
 * Menu dropdown do usuário com opções de Login, Criar Conta e Reset de Senha.
 * Exibe opções diferentes para usuários autenticados e não autenticados.
 * @component
 * @param {Object} props
 * @param {HTMLElement} props.anchorEl - Elemento âncora para o menu
 * @param {boolean} props.open - Se o menu está aberto
 * @param {Function} props.onClose - Callback para fechar menu
 * @returns {JSX.Element}
 */
function UserMenu({ anchorEl, open, onClose }) {
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector(state => state.auth);
  const [loginOpen, setLoginOpen] = useState(false);
  const [signUpOpen, setSignUpOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);

  const handleLoginClick = () => {
    onClose();
    setLoginOpen(true);
  };

  const handleSignUpClick = () => {
    onClose();
    setSignUpOpen(true);
  };

  const handleLogout = () => {
    dispatch(logoutAsync());
    onClose();
  };

  const handleProfileClick = () => {
    onClose();
    setProfileOpen(true);
  };

  const handleResetPasswordClick = () => {
    onClose();
    setResetPasswordOpen(true);
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {isAuthenticated
          ? [
              <MenuItem key='user-info' disabled>
                <ListItemIcon>
                  <AccountCircleOutlinedIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText>
                  <Typography variant='body2' noWrap>
                    {user?.nome || user?.email || ''}
                  </Typography>
                </ListItemText>
              </MenuItem>,
              <Divider key='divider' />,
              <MenuItem key='profile' onClick={handleProfileClick}>
                <ListItemIcon>
                  <SettingsOutlinedIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText>Meu Perfil</ListItemText>
              </MenuItem>,
              <MenuItem key='reset-password' onClick={handleResetPasswordClick}>
                <ListItemIcon>
                  <LockResetOutlinedIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText>Redefinir Senha</ListItemText>
              </MenuItem>,
              <MenuItem key='logout' onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutOutlinedIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText>Sair</ListItemText>
              </MenuItem>,
            ]
          : [
              <MenuItem key='login' onClick={handleLoginClick}>
                <ListItemIcon>
                  <LoginOutlinedIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText>Login</ListItemText>
              </MenuItem>,
              <MenuItem key='signup' onClick={handleSignUpClick}>
                <ListItemIcon>
                  <PersonAddOutlinedIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText>Criar Conta</ListItemText>
              </MenuItem>,
              <MenuItem key='reset-password' onClick={handleResetPasswordClick}>
                <ListItemIcon>
                  <LockResetOutlinedIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText>Redefinir Senha</ListItemText>
              </MenuItem>,
            ]}
      </Menu>

      <LoginForm open={loginOpen} onClose={() => setLoginOpen(false)} />
      <SignUpForm open={signUpOpen} onClose={() => setSignUpOpen(false)} />
      <UserProfile open={profileOpen} onClose={() => setProfileOpen(false)} />
      <ResetPasswordModal open={resetPasswordOpen} onClose={() => setResetPasswordOpen(false)} />
    </>
  );
}

export default UserMenu;
